// Board format for Explainer videos: one "board" (a hand-drawn-style slide
// whose elements pop in on the narration) per editor clip, stored in
// ve_media_items.metadata.board.
//
// This folder is shared by the in-app Player (src/components/video-editor)
// and the Lambda export bundle (remotion/), so it must stay self-contained:
// relative imports only, no `@/` aliases, and nothing beyond react, remotion
// and @remotion/* (both bundles provide those).

import type { FigureId, SkinTone } from './figures';

export const BOARD_SPEC_VERSION = 1;

export type BoardAspect = '16:9' | '9:16';

/**
 * A board's look (themes.ts): classic pops in, whiteboard and chalkboard are
 * drawn, and the premium looks (looks/) have their own renderers.
 */
export type ThemeId =
  | 'classic'
  | 'whiteboard'
  | 'chalkboard'
  | 'glass'
  | 'kinetic'
  | 'paper'
  | 'neon'
  | 'evidence'
  | 'newsprint'
  | 'blueprint'
  | 'notebook'
  | 'watercolor'
  | 'claymation'
  | 'engraving'
  | 'comic'
  | 'riso'
  | 'pixel'
  | 'stickman';

export const THEME_IDS: readonly ThemeId[] = [
  'classic',
  'whiteboard',
  'chalkboard',
  'glass',
  'kinetic',
  'paper',
  'neon',
  'evidence',
  'newsprint',
  'blueprint',
  'notebook',
  'watercolor',
  'claymation',
  'engraving',
  'comic',
  'riso',
  'pixel',
  'stickman',
];

/**
 * Looks illustrated for each video: their objects and characters are drawn
 * by an image model in the look's style (lib/explainer/art.ts) instead of
 * Fluent Emoji icons and figures. A board keeps an emoji for every drawn
 * object, so the other looks still draw it.
 */
export type ArtLook = 'watercolor' | 'claymation' | 'engraving';

export const ART_LOOKS: readonly ArtLook[] = [
  'watercolor',
  'claymation',
  'engraving',
];

export const isArtLook = (look: unknown): look is ArtLook =>
  typeof look === 'string' && (ART_LOOKS as readonly string[]).includes(look);

/** Seconds from the start of the board. Resolved when the project is made. */
export type At = number;

/**
 * Spec fields holding an `At`: `at` and any `…At` (`titleAt`). The planner
 * fills them with narration word indices, timing turns those into seconds.
 */
export const isTimeKey = (key: string) => /^at$|At$/.test(key);

export type CharacterColor =
  | 'green'
  | 'gray'
  | 'blue'
  | 'purple'
  | 'red'
  | 'orange'
  | 'brown'
  | 'beige';

export type Accessory = 'tie' | 'tophat' | 'flower';

/** How a bean character feels (its face); none is calm. */
export type CharacterMood = 'happy' | 'sad' | 'surprised' | 'angry';
export const CHARACTER_MOODS: readonly CharacterMood[] = [
  'happy',
  'sad',
  'surprised',
  'angry',
];

/** What a bean character does with its arms; none keeps them down. */
export type CharacterPose = 'wave' | 'point' | 'cheer' | 'think';
export const CHARACTER_POSES: readonly CharacterPose[] = [
  'wave',
  'point',
  'cheer',
  'think',
];

export type HighlightColor = 'green' | 'red';

export interface Highlight {
  word: string;
  color: HighlightColor;
}

export interface Line {
  at: At;
  text: string;
}

export interface CharacterSpec {
  color: CharacterColor;
  accessory?: Accessory;
  /**
   * Who the character is in looks that draw people (figures.ts), e.g.
   * "woman-teacher" or "cat"; the classic looks draw a bean in `color`.
   */
  figure?: FigureId;
  /** A person figure's skin tone. */
  tone?: SkinTone;
  /** Shown under the character, e.g. "accountant". */
  name?: string;
  /** When the character pops in (default: the board's start). */
  at?: At;
  /**
   * The character drawn for this video (`image:<id>`, a BoardImage with
   * `art`), in its mood and pose: shown in the look it was drawn in, the
   * bean or figure everywhere else.
   */
  art?: string;
  /** Speech lines; each replaces the previous one. */
  lines?: Line[];
  /** An icon the character holds. */
  prop?: { icon: string; side: 'left' | 'right'; at?: At };
  /** Its face (bean looks; people figures keep their own). */
  mood?: CharacterMood;
  /** Its arms (bean looks): waving, pointing, cheering or thinking. */
  pose?: CharacterPose;
}

export interface HeaderSpec {
  text: string;
  highlights?: Highlight[];
  at?: At;
  /** Already on screen from the previous board: no entrance animation. */
  carried?: boolean;
}

export interface IconSpec {
  icon: string;
  label?: string;
  at?: At;
}

export type Slot = 'left' | 'right' | 'top-left' | 'top-right';

export interface ExtraSpec extends IconSpec {
  slot: Slot;
}

export type ArrowColor = 'black' | 'red' | 'green';

export interface ArrowSpec {
  label?: string;
  color?: ArrowColor;
  icon?: string;
  at?: At;
}

export interface StatSpec {
  /** Counts up from 0 when it appears. */
  value: number;
  prefix?: string;
  suffix?: string;
  label: string;
  icon?: string;
  at?: At;
}

/**
 * A picture of the user's own (a logo, a product photo, a screenshot),
 * stored with the board. Any icon field may name one as `image:<id>`
 * (imageRef): it's drawn in the icon's place, framed by the look.
 */
export interface BoardImage {
  /** Lowercase letters and digits, unique within the board. */
  id: string;
  /** Our own copy (explainer/<user>/<project>/…), never the upload. */
  url: string;
  /** px of the stored file. */
  width: number;
  height: number;
  /**
   * Transparent around the subject (a logo, a cut-out product): drawn on
   * the board as it is, without a photo frame.
   */
  cutout?: boolean;
  /**
   * A cutout's own colour, light or dark: on a surface of the same tone it
   * sits on a plate of the other, or a white logo vanishes on a white board.
   */
  tone?: 'light' | 'dark';
  /**
   * A photograph of a scene or a person (not a screenshot, logo, product
   * or chart): print looks may reproduce it as a halftone newspaper photo
   * without losing anything the narration points at.
   */
  photo?: boolean;
  /**
   * A photo with too little brightness contrast for a halftone (flat, or
   * mostly foliage; measured when it's stored): print looks print it sharp.
   */
  lowContrast?: boolean;
  /**
   * A capture of a web page (a receipt board's source): print looks show it
   * as a clipping cut from the page, sharp, never as a halftone.
   */
  page?: boolean;
  /** What it shows ("The Acme logo"): alt text and the inspector. */
  label?: string;
  /**
   * Drawn for this video in an art look (lib/explainer/art.ts): the look
   * shows it only when it's the look it was drawn in, and any other look
   * draws `icon` (objects) or the character's bean or figure instead.
   */
  art?: BoardArt;
  /**
   * A real subject cut out of a photo (a person, a building, a product):
   * a cutout drawn with the look's outline around it, never on a plate.
   */
  subject?: boolean;
  /**
   * A subject cut-out's outline: a white silhouette a little larger than
   * the subject, the same size as the cut-out (which keeps room for it).
   */
  outline?: string;
  /**
   * A subject cut flat along the photo's bottom edge (a person from the
   * waist up): it stands on the board's bottom edge.
   */
  edge?: 'bottom';
  /**
   * Who made it and under which licence, as shown on screen: "Gage
   * Skidmore / Wikimedia Commons, CC BY-SA 3.0".
   */
  credit?: string;
  /** Where it came from (its file page), listed in the video's description. */
  source?: string;
}

/**
 * A stock clip stored with a footage board (explainer/<user>/<project>/…
 * .mp4 on the media CDN): it plays muted behind the board, from `from`
 * seconds into the clip.
 */
export interface BoardVideo {
  url: string;
  /** A frame of the clip (JPEG): stills show it, and a clip that fails. */
  poster?: string;
  /** px of the stored clip. */
  width: number;
  height: number;
  /** Seconds the stored clip lasts. */
  duration: number;
  /** Seconds into the clip where the board starts it. */
  from?: number;
  /** What it shows ("Traders on the Nasdaq floor"). */
  label?: string;
  /** "Video: Jane Doe / Pexels". */
  credit?: string;
  /** Where it came from (its page), listed in the video's description. */
  source?: string;
}

export interface BoardArt {
  /** The look it was drawn in. */
  look: ArtLook;
  /** The video's art item it is (the art set's key), for restyling. */
  key: string;
  /** An object's stand-in emoji icon in the looks that don't draw art. */
  icon?: string;
}

const IMAGE_REF_PREFIX = 'image:';

/** The icon value that shows a board image. */
export const imageRef = (id: string) => `${IMAGE_REF_PREFIX}${id}`;

/** The image id an icon value names, if it names one. */
export const imageRefId = (name: string | undefined): string | undefined =>
  name?.startsWith(IMAGE_REF_PREFIX)
    ? name.slice(IMAGE_REF_PREFIX.length) || undefined
    : undefined;

/** Images a board carries at most (the form takes 6 uploads). */
export const BOARD_IMAGES_MAX = 6;

/**
 * Pictures one board may carry: the user's pictures, a receipt or photo,
 * and in the art looks a drawing for every object and character (a dialog
 * holds 4 characters, 4 props and 2 extras).
 */
export const BOARD_IMAGES_PER_BOARD = 16;

export interface ListItemSpec {
  icon: string;
  text: string;
  highlights?: Highlight[];
  at?: At;
}

interface BoardBase {
  version: typeof BOARD_SPEC_VERSION;
  /** The canvas the board was laid out for. */
  aspect: BoardAspect;
  /** The look; missing means classic (boards made before looks existed). */
  theme?: ThemeId;
  /**
   * The narration's language (ISO 639-1), which picks the handwriting font
   * for its alphabet, so every board of a video uses the same one.
   */
  lang?: string;
  header?: HeaderSpec;
  /**
   * The narration's words spoken during the board, as written in the script
   * (punctuation included), each with its time: [seconds from the board's
   * start, word]. Kinetic Type sets them on screen.
   */
  words?: BoardWord[];
  /** false turns the look's sound effects off (looks/ renderers play them). */
  sfx?: boolean;
  /**
   * 'film': the board as if shot on film, over any look: grain, a vignette,
   * a slight flicker and gate weave.
   */
  finish?: 'film';
  /** The user's pictures this board shows (icon fields name them). */
  images?: BoardImage[];
}

/** [seconds from the board's start, the word as written in the script]. */
export type BoardWord = [At, string];

export type DialogBoard = BoardBase & {
  template: 'dialog';
  characters: CharacterSpec[];
  extras?: ExtraSpec[];
};
export type TitleBoard = BoardBase & {
  template: 'title';
  character: CharacterSpec;
  title: string;
  titleAt?: At;
  /**
   * A picture of the user's (`image:<id>`, usually their logo or product)
   * shown in the character's place.
   */
  image?: string;
};
export type ChapterBoard = BoardBase & {
  template: 'chapter';
  title: string;
  icon?: string;
  /**
   * Which chapter card of the video this is (1-based), stamped when boards
   * are timed: Pixel Quest shows it as "LEVEL 2".
   */
  level?: number;
};
export type SplitBoard = BoardBase & {
  template: 'split';
  source: IconSpec;
  branches: (IconSpec & { color?: HighlightColor })[];
};
export type CycleBoard = BoardBase & {
  template: 'cycle';
  arrowStyle: 'straight' | 'curved';
  actor: CharacterSpec;
  out: ArrowSpec;
  target: { icons: string[]; label?: string; at?: At };
  back: ArrowSpec;
};
export type StatsBoard = BoardBase & { template: 'stats'; stats: StatSpec[] };
export type ListBoard = BoardBase & {
  template: 'list';
  items: ListItemSpec[];
  character?: CharacterSpec;
};

/**
 * `bars`: columns (rows in portrait) that grow to their values. `line`: a
 * trend drawn point by point. `donut`: parts of a whole (2-3). `pictogram`:
 * "7 in 10" as icons.
 */
export type ChartKind = 'bars' | 'line' | 'donut' | 'pictogram';

export const CHART_KINDS: readonly ChartKind[] = [
  'bars',
  'line',
  'donut',
  'pictogram',
];

export interface ChartDatum {
  /** What the value is ("2020", "Apple", "Women"). */
  label: string;
  /** The number, read from the narration (scale applied). */
  value: number;
  /** The value as shown ("$355K", "40%", "1,2 Mio."). */
  display: string;
  icon?: string;
  /** The value the board is about: drawn in the accent colour. */
  highlight?: boolean;
  at?: At;
}

export type ChartBoard = BoardBase & {
  template: 'chart';
  kind: ChartKind;
  data: ChartDatum[];
  /** The values are percentages: a donut is a share of 100. */
  percent?: boolean;
  /** Pictogram: how many icons make the whole (2-20, default 10). */
  total?: number;
  /** A short source line under the chart ("Source: World Bank"). */
  source?: string;
  /** When the axis or the empty ring appears (default: with the board). */
  at?: At;
};

export interface TimelineEvent {
  /** As shown: "1969", "1990s". */
  year: string;
  label: string;
  icon?: string;
  at?: At;
}

export type TimelineBoard = BoardBase & {
  template: 'timeline';
  events: TimelineEvent[];
  /** When the axis appears (default: with the board). */
  at?: At;
};

/** A map's colours, from the look's palette. */
export type MapColor = 'green' | 'orange' | 'purple' | 'red' | 'blue';

/** In the order places get them when the planner gives none. */
export const MAP_COLORS: readonly MapColor[] = [
  'green',
  'orange',
  'purple',
  'red',
  'blue',
];

/** A point in map units (explainer/maps.ts: Miller, 100 per degree). */
export type MapPoint = [number, number];
/** [left, top, right, bottom] in map units. */
export type MapBox = [number, number, number, number];

/** What an area is (the planner reads it; the editor names it). */
export type MapAreaType = 'island' | 'state' | 'province' | 'region';

export interface MapPlaceSpec {
  /**
   * A country is coloured in, a region (a continent, the EU, Southeast
   * Asia) colours in all its countries, an area (an island, a state or
   * province, a region like Kalimantan or the Sahara) colours in its own
   * shape, a city is a dot.
   */
  kind: 'country' | 'region' | 'area' | 'city';
  /** A country's id in the map data (Natural Earth's ADM0_A3: "FRA"). */
  id?: string;
  /** A region's countries. */
  ids?: string[];
  /** An area's key in the gazetteer ("sumatra", "us-ca"). */
  area?: string;
  type?: MapAreaType;
  /**
   * An area's shape: an SVG path in map units (M, l and z), its coasts the
   * world map's own. Boards carry it, so the map file stays as it is.
   */
  d?: string;
  label: string;
  /** Where the label (country, region, area) or the dot (city) goes. */
  point: MapPoint;
  /**
   * Where the name goes when not on `point` (a country whose usual spot is
   * on one of its coloured islands); routes still start at `point`.
   */
  labelAt?: MapPoint;
  /** What the camera frames: a country's main territory, a region's middle. */
  box?: MapBox;
  color?: MapColor;
  at?: At;
}

export interface MapRouteSpec {
  /** Indices into the board's places. */
  from: number;
  to: number;
  color?: MapColor;
  at?: At;
}

/**
 * Another view of places an earlier map showed: "wide" pulls out to their
 * surroundings, "close" moves in closer.
 */
export type MapViewStyle = 'wide' | 'close';

export type MapBoard = BoardBase & {
  template: 'map';
  places: MapPlaceSpec[];
  /** Arrows from one place to another (trade, travel, migration). */
  routes?: MapRouteSpec[];
  /** Set on a map repeating an earlier map's places (planner.ts). */
  view?: MapViewStyle;
};

export interface QuizOptionSpec {
  text: string;
  icon?: string;
  at?: At;
}

/**
 * A question (the header) with 2-4 options; a 3-2-1 countdown runs in the
 * seconds before `revealAt`, when the right option turns green.
 */
export type QuizBoard = BoardBase & {
  template: 'quiz';
  header: HeaderSpec;
  options: QuizOptionSpec[];
  /** Index of the right option. */
  answer: number;
  revealAt: At;
};

export type VersusSideName = 'left' | 'right';

export interface VersusSideSpec {
  label: string;
  icon: string;
  at?: At;
}

/** One point compared: what it is, and each side's value. */
export interface VersusRowSpec {
  label: string;
  left: string;
  right: string;
  /** The side that wins this point: its value is drawn in green. */
  winner?: VersusSideName;
  at?: At;
}

/** Two things side by side ("Coffee vs Tea"), compared point by point. */
export type VersusBoard = BoardBase & {
  template: 'versus';
  /** Left (top in portrait) and right. */
  sides: [VersusSideSpec, VersusSideSpec];
  rows?: VersusRowSpec[];
  /** The overall winner, crowned at `winnerAt`. */
  winner?: VersusSideName;
  winnerAt?: At;
};

export interface RankItemSpec {
  /** 1 is the top (a board may hold 6-10 of a longer ranking). */
  rank: number;
  text: string;
  icon?: string;
  at?: At;
}

/**
 * A ranking ("Top 5…"): numbered slots shown empty from the start, each
 * filled as its item is named (usually counting down to number 1).
 */
export type RankingBoard = BoardBase & {
  template: 'ranking';
  items: RankItemSpec[];
};

/**
 * One of the user's pictures, large (a screenshot, a product, a photo),
 * under the header with a caption below it.
 */
export type PictureBoard = BoardBase & {
  template: 'picture';
  /** `image:<id>` */
  image: string;
  /** When the picture appears (default: with the board). */
  at?: At;
  /** A short line under the picture. */
  caption?: string;
  captionAt?: At;
};

/** A rectangle on a picture, in the picture's own px: [left, top, width, height]. */
export type PictureRect = [number, number, number, number];

/**
 * A "receipt": one of the video's sources on screen, a capture of the page
 * (its headline and the quoted passage), the quote swept with a highlighter
 * on the word the narrator says it, the site named under it.
 */
export type ReceiptBoard = BoardBase & {
  template: 'receipt';
  /** `image:<id>`: the capture (a BoardImage with `page: true`). */
  image: string;
  /** When the capture appears (default: with the board). */
  at?: At;
  /** The quoted sentence as the page writes it (alt text, Kinetic Type). */
  quote: string;
  /** The quote's lines on the capture, one rectangle each. */
  marks: PictureRect[];
  /** When the highlighter sweeps the quote. */
  markAt?: At;
  /** The site's name ("Ember", "USDA Foreign Agricultural Service"). */
  site: string;
  /** The page's address as shown ("ember-energy.org/latest-insights/…"). */
  url?: string;
};

/**
 * A photograph (a licensed stock photo), large, pushed in slowly while it's
 * on screen, with a short caption and the photographer's credit.
 */
export type PhotoBoard = BoardBase & {
  template: 'photo';
  /** `image:<id>`: the photo (a BoardImage with `photo: true`). */
  image: string;
  at?: At;
  caption?: string;
  captionAt?: At;
  /** "Photo: Jane Doe / Pexels". */
  credit?: string;
  /** Who the photo shows, as a lower-third card. */
  nameCard?: NameCardSpec;
};

/**
 * Who someone (or something) on a board is, as a lower-third card: "Garry
 * Tan", "President, Y Combinator".
 */
export interface NameCardSpec {
  name: string;
  role?: string;
  at?: At;
}

/** A word or two set big beside a subject or over footage, on its word. */
export interface KeywordSpec {
  text: string;
  at?: At;
}

/** Where a cut-out subject stands on the board. */
export type SubjectSide = 'left' | 'right' | 'center';

/**
 * A real person (or place, or thing) cut out of a photo, standing in front
 * of a background photo, a big line of text behind them and a few words
 * beside them.
 */
export type SubjectBoard = BoardBase & {
  template: 'subject';
  /** `image:<id>`: the cut-out (a BoardImage with `subject: true`). */
  image: string;
  /** When the subject appears (default: with the board). */
  at?: At;
  /** `image:<id>`: the photo behind them; none: the look's own colour. */
  background?: string;
  /** The big line behind the subject ("GARRY TAN", "FREMONT, 1981"). */
  back?: string;
  backAt?: At;
  /** 1-3 short words beside the subject, each on its word. */
  keywords?: KeywordSpec[];
  /** Where the subject stands (default: right; portrait: bottom centre). */
  side?: SubjectSide;
  nameCard?: NameCardSpec;
};

/** A line drawn rising or falling over footage, on its word. */
export interface TrendSpec {
  direction: 'up' | 'down';
  at?: At;
}

/**
 * A ring drawn around a spot of the footage (an aerial's town), its centre
 * and radius as shares of the frame's width and height.
 */
export interface RingSpec {
  x: number;
  y: number;
  r: number;
  label?: string;
  at?: At;
}

/**
 * A stock clip filling the board, slowly pushed in and darkened, with
 * something over it: a number counting up, a rising line, a ring around a
 * spot, a few big words.
 */
export type FootageBoard = BoardBase & {
  template: 'footage';
  video: BoardVideo;
  stat?: StatSpec;
  trend?: TrendSpec;
  ring?: RingSpec;
  keywords?: KeywordSpec[];
  caption?: string;
  captionAt?: At;
  nameCard?: NameCardSpec;
};

/** A company's or product's logo on a logos board. */
export interface LogoSpec {
  /** `image:<id>`: the logo (a cutout). */
  image: string;
  /** Its name under the card ("Stripe"). */
  label?: string;
  at?: At;
}

/**
 * Logos of the companies or products the narration names, on cards that
 * pop in on their words: one logo is a reveal, 2-6 a grid.
 */
export type LogosBoard = BoardBase & {
  template: 'logos';
  logos: LogoSpec[];
};

export interface WallPhotoSpec {
  /** `image:<id>`: a photo (or a subject cut-out, shown on its tile). */
  image: string;
  at?: At;
}

/**
 * A wall of photos (the people, places and moments of the story) that the
 * camera moves into, until one photo fills the board.
 */
export type WallBoard = BoardBase & {
  template: 'wall';
  photos: WallPhotoSpec[];
  /** Index of the photo the camera moves into. */
  focus: number;
  /** When the camera starts moving in (default: halfway through). */
  focusAt?: At;
  caption?: string;
  captionAt?: At;
  nameCard?: NameCardSpec;
};

/**
 * A framed portrait: a photo in a frame, the subject cut out of it stepping
 * out over the frame's top edge.
 */
export type PortraitBoard = BoardBase & {
  template: 'portrait';
  /** `image:<id>`: the cut-out (a BoardImage with `subject: true`). */
  image: string;
  /** `image:<id>`: the photo inside the frame; none: the look's colour. */
  background?: string;
  at?: At;
  /** When the subject steps out of the frame (default: just after `at`). */
  breakAt?: At;
  caption?: string;
  captionAt?: At;
  nameCard?: NameCardSpec;
};

export type BoardSpec =
  | DialogBoard
  | TitleBoard
  | ChapterBoard
  | SplitBoard
  | CycleBoard
  | StatsBoard
  | ListBoard
  | ChartBoard
  | TimelineBoard
  | MapBoard
  | QuizBoard
  | VersusBoard
  | RankingBoard
  | PictureBoard
  | ReceiptBoard
  | PhotoBoard
  | SubjectBoard
  | FootageBoard
  | LogosBoard
  | WallBoard
  | PortraitBoard;

export type BoardTemplate = BoardSpec['template'];

export const BOARD_TEMPLATES: readonly BoardTemplate[] = [
  'dialog',
  'title',
  'chapter',
  'split',
  'cycle',
  'stats',
  'list',
  'chart',
  'timeline',
  'map',
  'quiz',
  'versus',
  'ranking',
  'picture',
  'receipt',
  'photo',
  'subject',
  'footage',
  'logos',
  'wall',
  'portrait',
];

/**
 * Templates showing real media found for the board (a documentary's
 * photos, cut-outs, logos and footage): a redo can't find them again.
 */
export const MEDIA_TEMPLATES: readonly BoardTemplate[] = [
  'receipt',
  'photo',
  'subject',
  'footage',
  'logos',
  'wall',
  'portrait',
];

/** How many places and routes a map shows. */
export const MAP_LIMITS = { places: 6, routes: 3 } as const;

/** Quiz options, versus rows and ranking items a board shows: [min, max]. */
export const QUIZ_OPTIONS: [number, number] = [2, 4];
export const VERSUS_ROWS = 3;
export const rankingRange = (aspect: BoardAspect): [number, number] =>
  aspect === '9:16' ? [2, 5] : [2, 5];

/** Seconds the quiz countdown takes at most (one per number). */
export const QUIZ_COUNTDOWN = 3;

/** How many values a chart of a kind shows on a canvas: [min, max]. */
export function chartDataRange(
  kind: ChartKind,
  aspect: BoardAspect,
): [number, number] {
  const portrait = aspect === '9:16';
  switch (kind) {
    case 'bars':
      return [2, portrait ? 5 : 6];
    case 'line':
      return [3, portrait ? 6 : 8];
    case 'donut':
      return [2, 3];
    case 'pictogram':
      return [1, 1];
  }
}

/** How many events a timeline shows on a canvas: [min, max]. */
export const timelineRange = (aspect: BoardAspect): [number, number] =>
  aspect === '9:16' ? [2, 4] : [2, 5];

export const CHARACTER_COLORS: readonly CharacterColor[] = [
  'green',
  'gray',
  'blue',
  'purple',
  'red',
  'orange',
  'brown',
  'beige',
];

export const ACCESSORIES: readonly Accessory[] = ['tie', 'tophat', 'flower'];

/** Per-template caps; the renderer drops anything beyond them. */
export const BOARD_LIMITS = {
  dialogCharacters: 4,
  extras: 2,
  lines: 6,
  branches: 2,
  cycleIcons: 4,
  stats: 3,
  listItems: 4,
  highlights: 4,
  words: 600,
  /** A receipt's quote: lines highlighted on the capture. */
  receiptMarks: 12,
  /** Words beside a subject or over footage. */
  keywords: 3,
  /** Logos on a logos board. */
  logos: 6,
  /** Photos on a wall. */
  wallPhotos: 9,
} as const;

/** Photos a wall needs: fewer reads as a few pictures, not a wall. */
export const WALL_MIN_PHOTOS = 3;

/**
 * A footage board's clip while it's being found (a documentary's planned
 * board, before its media is resolved): never stored, never played.
 */
export const PENDING_VIDEO_URL =
  'https://media.easy-peasy.ai/explainer/pending/clip.mp4';
